import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import salairesApi from '../services/salaires';
import tableauApi from '../services/tableau';
import './TableauOrga.css';

// Excalidraw est lourd : on ne le charge que lorsqu'on ouvre l'onglet.
const Excalidraw = lazy(() => import('@excalidraw/excalidraw').then(m => ({ default: m.Excalidraw })));
// Le paquet exporte aussi des helpers ; on les charge à la demande.
let helpers = null;
async function chargerHelpers() {
  if (!helpers) helpers = await import('@excalidraw/excalidraw');
  return helpers;
}

// Palette de couleurs par défaut pour les nouvelles cartes (Excalidraw les
// laisse ensuite modifier nativement).
const COULEURS_CARTE = ['#e7f5ff', '#fff9db', '#ebfbee', '#fff0f6', '#f3f0ff', '#fff4e6'];

export default function TableauOrga({ user, isAdmin }) {
  const [salaries, setSalaries] = useState([]);
  const [recherche, setRecherche] = useState('');
  const [pretScene, setPretScene] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const apiRef = useRef(null);           // excalidrawAPI
  const sauvegardeTimer = useRef(null);
  const dernierEnvoi = useRef('');       // hash de la dernière scène envoyée
  const compteurInsert = useRef(0);

  // Charger salariés + scène en parallèle
  useEffect(() => {
    salairesApi.getSalaries({}).then(r => setSalaries(r.data || [])).catch(() => {});
    tableauApi.get()
      .then(r => {
        const scene = r.data && r.data.scene;
        if (scene && Array.isArray(scene.elements)) {
          setInitialData({ elements: scene.elements, appState: { ...(scene.appState || {}), collaborators: undefined }, files: scene.files || undefined, scrollToContent: true });
        } else {
          setInitialData({ elements: [] });
        }
        setPretScene(true);
      })
      .catch(() => { setInitialData({ elements: [] }); setPretScene(true); });
  }, []);

  // Sauvegarde anti-rebond (admin seulement)
  const onChange = useCallback((elements, appState, files) => {
    if (!isAdmin || !apiRef.current) return;
    if (sauvegardeTimer.current) clearTimeout(sauvegardeTimer.current);
    sauvegardeTimer.current = setTimeout(async () => {
      const { getSceneVersion } = await chargerHelpers();
      let version;
      try { version = getSceneVersion(elements); } catch { version = String(elements.length); }
      const cle = String(version);
      if (cle === dernierEnvoi.current) return;   // rien de neuf
      dernierEnvoi.current = cle;
      // On ne conserve de l'appState que le strict utile (pas d'état volatil)
      const scene = {
        elements,
        appState: { viewBackgroundColor: appState.viewBackgroundColor, gridSize: appState.gridSize },
        files: files || {},
      };
      try { await tableauApi.save(scene); } catch { /* silencieux : réessaiera au prochain change */ }
    }, 800);
  }, [isAdmin]);

  useEffect(() => () => { if (sauvegardeTimer.current) clearTimeout(sauvegardeTimer.current); }, []);

  // Insertion d'une carte salarié au centre de la vue
  const insererCarte = useCallback(async (s) => {
    const api = apiRef.current;
    if (!api) return;
    const { convertToExcalidrawElements } = await chargerHelpers();
    const libelle = `${s.nom || ''} ${s.prenom || ''}`.trim();
    const sousTitre = s.poste_libelle || s.service_libelle || '';
    const texte = sousTitre ? `${libelle}\n${sousTitre}` : libelle;
    const couleur = COULEURS_CARTE[compteurInsert.current % COULEURS_CARTE.length];
    compteurInsert.current += 1;

    // Position : centre de la vue, décalé en cascade pour ne pas empiler
    const aps = api.getAppState();
    const cx = (-aps.scrollX + aps.width / 2 / aps.zoom.value);
    const cy = (-aps.scrollY + aps.height / 2 / aps.zoom.value);
    const dec = (compteurInsert.current % 6) * 16;
    const x = cx - 90 + dec;
    const y = cy - 30 + dec;

    const nouveaux = convertToExcalidrawElements([
      {
        type: 'rectangle', x, y, width: 180, height: 62,
        backgroundColor: couleur, strokeColor: '#1e1e1e', fillStyle: 'solid',
        roundness: { type: 3 }, strokeWidth: 1,
        label: { text: texte, fontSize: 14, textAlign: 'center', verticalAlign: 'middle' },
      },
    ]);
    const elements = [...api.getSceneElements(), ...nouveaux];
    api.updateScene({ elements });
    // laisser onChange déclencher la sauvegarde
  }, []);

  const salariesFiltres = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    const actifs = salaries.filter(s => s.actif !== false);
    if (!q) return actifs.slice(0, 400);
    return actifs.filter(s => `${s.nom || ''} ${s.prenom || ''} ${s.poste_libelle || ''} ${s.service_libelle || ''}`.toLowerCase().includes(q)).slice(0, 400);
  }, [salaries, recherche]);

  const insererService = useCallback(async () => {
    // Ajoute d'un coup les cartes des salariés filtrés (max 40), à réagencer ensuite
    if (!apiRef.current || !salariesFiltres.length) return;
    for (const s of salariesFiltres.slice(0, 40)) {
      await insererCarte(s);
    }
  }, [insererCarte, salariesFiltres]);

  return (
    <div className="tbl-page">
      <aside className="tbl-palette">
        <div className="tbl-palette-tete">
          <input className="tbl-recherche" placeholder="🔍 Salarié, poste…" value={recherche} onChange={e => setRecherche(e.target.value)} />
          {isAdmin && salariesFiltres.length > 0 && recherche.trim() && (
            <button className="tbl-inserer-tout" onClick={insererService} title="Insérer les salariés filtrés (max 40)">
              + Insérer les {Math.min(salariesFiltres.length, 40)} résultats
            </button>
          )}
        </div>
        {!isAdmin && <div className="tbl-lecture">Lecture seule</div>}
        <div className="tbl-liste">
          {salariesFiltres.map(s => (
            <button key={s.id} className="tbl-item" disabled={!isAdmin}
              onClick={() => isAdmin && insererCarte(s)}
              title={isAdmin ? 'Cliquer pour ajouter au tableau' : 'Lecture seule'}>
              <span className="tbl-item-nom">{s.nom} {s.prenom || ''}</span>
              {(s.poste_libelle || s.service_libelle) && <span className="tbl-item-poste">{s.poste_libelle || s.service_libelle}</span>}
            </button>
          ))}
          {salariesFiltres.length === 0 && <div className="tbl-vide">Aucun salarié</div>}
        </div>
        <div className="tbl-aide">
          {isAdmin
            ? 'Cliquez un salarié pour l’ajouter. Sur le tableau : glissez, encadrez (touche F), clic droit, couleurs — tout est natif. Sauvegarde automatique.'
            : 'Tableau en lecture seule.'}
        </div>
      </aside>

      <div className="tbl-canevas">
        {pretScene ? (
          <Suspense fallback={<div className="tbl-chargement">Chargement du tableau…</div>}>
            <Excalidraw
              excalidrawAPI={api => { apiRef.current = api; }}
              initialData={initialData}
              onChange={onChange}
              viewModeEnabled={!isAdmin}
              langCode="fr-FR"
              UIOptions={{ canvasActions: { loadScene: false, saveToActiveFile: false } }}
            />
          </Suspense>
        ) : (
          <div className="tbl-chargement">Chargement du tableau…</div>
        )}
      </div>
    </div>
  );
}
